import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    fetchGatePasses,
    gatePassStatusLabel,
    gatePassTypeLabel,
    humanizeGatePassValue,
    GATE_PASS_STATUS_OPTIONS,
    GATE_PASS_TYPE_OPTIONS,
    type GatePassApiRecord,
    type GatePassListMeta,
} from '../../services/gatepassApi';
import { fetchStudents, mapStudentToUi, type StudentApiRecord } from '../../services/studentApi';
import type { ApiError } from '../../services/api';
import './GatePass.css';

const PAGE_SIZE_OPTIONS = [15, 25, 50];

/* ═══════════════════════════════════════════════════════════════
   Print — thermal gate-pass slip (reused from the issue flow)
   ═══════════════════════════════════════════════════════════════ */
function formatPrintDate(dateStr?: string | null): string {
    const d = dateStr ? new Date(dateStr) : new Date();
    const safe = isNaN(d.getTime()) ? new Date() : d;
    return safe.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface GatePassPrintData {
    schoolName: string;
    studentName: string;
    enrollmentNumber: string | null;
    className: string | null;
    sectionName: string | null;
    photoUrl: string | null;
    status: string;
    passType: string;
    reason: string;
    expectedExit: string;
    expectedReturn: string | null;
    authorizedName: string | null;
    authorizedRelation: string | null;
    authorizedContact: string | null;
    validDate: string | null;
}

/** Escape text before injecting it into the print document's HTML. */
function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (c) => {
        switch (c) {
            case '&': return '&amp;';
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '"': return '&quot;';
            default: return '&#39;';
        }
    });
}

function openGatePassPrintWindow(data: GatePassPrintData) {
    const dateStr = formatPrintDate(data.validDate);
    const classSection = [data.className, data.sectionName].filter(Boolean).join(' – ') || '—';

    // Photo block — only when the pass actually carries one. The image is
    // escaped and printing is deferred until it loads (see the script below).
    const photoHtml = data.photoUrl
        ? `<div class="photo"><img src="${escapeHtml(data.photoUrl)}" alt="Gate pass photo" /></div>`
        : '';

    const detailRow = (label: string, value: string | null): string =>
        value ? `<div class="row"><span class="label">${label}</span><span class="value">${escapeHtml(value)}</span></div>` : '';

    const authorizedValue = data.authorizedName
        ? data.authorizedName + (data.authorizedRelation ? ` (${data.authorizedRelation})` : '')
        : null;

    const returnTimeHtml = data.expectedReturn
        ? `<div class="time-item"><div class="time-label">Expected Return</div><div class="time-value">${escapeHtml(data.expectedReturn)}</div></div>`
        : '';

    const html = `<!DOCTYPE html>
<html>
<head>
<title>Gate Pass — ${escapeHtml(data.studentName)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1e293b; padding: 6px 8px; max-width: 300px; margin: 0 auto; line-height: 1.25; }
  .header { text-align: center; padding-bottom: 5px; margin-bottom: 5px; border-bottom: 2px double #1e293b; }
  .header .icon { font-size: 18px; margin-bottom: 1px; }
  .header h1 { font-size: 14px; font-weight: 800; letter-spacing: -0.02em; text-transform: uppercase; }
  .header .sub { font-size: 9px; color: #6b7280; }
  .pass-badge { text-align: center; margin: 5px 0; }
  .pass-badge span { display: inline-block; background: #1e293b; color: #fff; font-size: 12px; font-weight: 800; padding: 3px 22px; border-radius: 4px; letter-spacing: 0.12em; text-transform: uppercase; }
  .photo { text-align: center; margin: 5px 0; }
  .photo img { width: 88px; height: 88px; object-fit: cover; border: 1.5px solid #1e293b; border-radius: 6px; }
  .student-block { text-align: center; margin: 5px 0; padding: 5px 6px; border: 1.5px solid #1e293b; border-radius: 6px; }
  .student-block .name { font-size: 15px; font-weight: 800; letter-spacing: -0.01em; }
  .student-block .meta { font-size: 9px; color: #475569; margin-top: 1px; }
  .section { margin-bottom: 5px; }
  .section-title { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; margin-bottom: 2px; border-bottom: 1px solid #e5e7eb; padding-bottom: 1px; }
  .row { display: flex; justify-content: space-between; font-size: 11px; padding: 1px 0; gap: 8px; }
  .label { color: #6b7280; flex-shrink: 0; }
  .value { font-weight: 600; text-align: right; word-break: break-word; }
  .reason-highlight { text-align: center; margin: 5px 0; padding: 5px 6px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; }
  .reason-highlight .reason-label { font-size: 8px; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; }
  .reason-highlight .reason-value { font-size: 13px; font-weight: 800; margin-top: 1px; }
  .time-block { display: flex; justify-content: space-around; margin: 5px 0; text-align: center; }
  .time-block .time-label { font-size: 8px; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; }
  .time-block .time-value { font-size: 16px; font-weight: 800; font-variant-numeric: tabular-nums; margin-top: 1px; }
  .signatures { display: flex; justify-content: space-between; margin-top: 14px; padding-top: 4px; }
  .sig-box { text-align: center; width: 45%; }
  .sig-line { border-top: 1px solid #1e293b; padding-top: 3px; font-size: 9px; color: #6b7280; }
  .footer { text-align: center; margin-top: 6px; padding-top: 4px; border-top: 1px dashed #d1d5db; }
  .footer p { font-size: 8px; color: #9ca3af; }
  .footer .date { font-size: 10px; font-weight: 600; color: #475569; margin-bottom: 1px; }
  .cut-line { border-top: 1px dashed #9ca3af; margin: 5px 0 3px; }
  @media print { body { padding: 2px; } }
</style>
</head>
<body>
  <div class="header"><div class="icon">🏫</div><h1>${escapeHtml(data.schoolName)}</h1><div class="sub">Gate Pass</div></div>
  <div class="pass-badge"><span>${escapeHtml(data.status)}</span></div>
  ${photoHtml}
  <div class="student-block">
    <div class="name">${escapeHtml(data.studentName)}</div>
    <div class="meta">${data.enrollmentNumber ? `Enrollment: ${escapeHtml(data.enrollmentNumber)} · ` : ''}${escapeHtml(classSection)}</div>
  </div>
  <div class="reason-highlight">
    <div class="reason-label">Reason for Leaving</div>
    <div class="reason-value">${escapeHtml(data.reason)}</div>
  </div>
  <div class="time-block">
    <div class="time-item"><div class="time-label">Expected Exit</div><div class="time-value">${escapeHtml(data.expectedExit)}</div></div>
    ${returnTimeHtml}
  </div>
  <div class="section">
    <div class="section-title">Details</div>
    ${detailRow('Pass Type', data.passType)}
    ${detailRow('Authorized By', authorizedValue)}
    ${detailRow('Contact', data.authorizedContact)}
  </div>
  <div class="signatures">
    <div class="sig-box"><div class="sig-line">Authorized By</div></div>
    <div class="sig-box"><div class="sig-line">Guardian Sign</div></div>
  </div>
  <div class="cut-line"></div>
  <div class="footer">
    <p class="date">Valid: ${escapeHtml(dateStr)}</p>
    <p>This gate pass is valid for the date shown.</p>
    <p>Please return the student on time.</p>
  </div>
</body>
<script>
  (function () {
    function doPrint() { window.focus(); window.print(); }
    window.onload = function () {
      var img = document.querySelector('.photo img');
      if (img && !img.complete) {
        img.onload = doPrint;
        img.onerror = function () { img.style.display = 'none'; doPrint(); };
      } else {
        doPrint();
      }
    };
  })();
</script>
</html>`;

    const w = window.open('', '_blank', 'width=360,height=600');
    if (w) {
        w.document.write(html);
        w.document.close();
    }
}

/* ═══════════════════════════════════════════════════════════════
   Searchable / plain dropdown
   ═══════════════════════════════════════════════════════════════ */
type SelectOption = { value: string; label: string };

function SearchableSelect({
    options,
    value,
    onChange,
    placeholder,
    searchPlaceholder,
    searchable = true,
    id,
}: {
    options: SelectOption[];
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    searchPlaceholder?: string;
    searchable?: boolean;
    id?: string;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selected = options.find((o) => o.value === value);
    const filtered = searchable && query.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
        : options;

    return (
        <div className="gatepass__ss" ref={wrapRef}>
            <button type="button" id={id} className="gatepass__ss-trigger" onClick={() => setOpen((o) => !o)}>
                <span className={selected && selected.value ? '' : 'gatepass__ss-placeholder'}>
                    {selected ? selected.label : placeholder}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {open && (
                <div className="gatepass__ss-menu">
                    {searchable && (
                        <input
                            className="gatepass__ss-search"
                            type="text"
                            autoFocus
                            placeholder={searchPlaceholder}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    )}
                    <div className="gatepass__ss-list">
                        {filtered.length === 0 ? (
                            <div className="gatepass__ss-empty">No matches</div>
                        ) : (
                            filtered.map((o) => (
                                <button
                                    type="button"
                                    key={o.value || '__all__'}
                                    className={`gatepass__ss-option${o.value === value ? ' gatepass__ss-option--active' : ''}`}
                                    onClick={() => {
                                        onChange(o.value);
                                        setOpen(false);
                                        setQuery('');
                                    }}
                                >
                                    {o.label}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════
   Record → display helpers
   ═══════════════════════════════════════════════════════════════ */
function studentNameOf(gp: GatePassApiRecord): string {
    const s = gp.student;
    if (!s) return '—';
    if (s.name) return s.name;
    const full = `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim();
    if (full) return full;
    // The list endpoint can return a null name — fall back to the enrollment no.
    return s.enrollment_number || '—';
}

function studentMetaOf(gp: GatePassApiRecord): string | null {
    const s = gp.student;
    if (!s) return null;
    const hasRealName = !!(s.name || `${s.first_name ?? ''}${s.last_name ?? ''}`.trim());
    const classSection = [s.current_class?.name, s.current_section?.name].filter(Boolean).join(' – ');
    const parts: string[] = [];
    // Only show the enrollment here when it isn't already the primary name.
    if (hasRealName && s.enrollment_number) parts.push(s.enrollment_number);
    if (classSection) parts.push(classSection);
    return parts.length ? parts.join(' · ') : null;
}

function parentNameOf(gp: GatePassApiRecord): string | null {
    const p = gp.student?.parent;
    if (!p) return null;
    if (p.name) return p.name;
    const full = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
    return full || null;
}

function parentContactOf(gp: GatePassApiRecord): string | null {
    const p = gp.student?.parent;
    if (!p) return null;
    // Field name is unconfirmed — accept the most likely contact keys.
    return p.contact_number || p.phone || p.phone_number || p.mobile || p.contact || null;
}

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(t: string | null | undefined): string {
    if (!t) return '—';
    if (/^\d{2}:\d{2}/.test(t)) return t.slice(0, 5);
    const d = new Date(t);
    return isNaN(d.getTime()) ? t : d.toTimeString().slice(0, 5);
}

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */
export default function GatePass() {
    const { t } = useTranslation();

    /* ── List state ── */
    const [gatePasses, setGatePasses] = useState<GatePassApiRecord[]>([]);
    const [meta, setMeta] = useState<GatePassListMeta>({ current_page: 1, from: 1, last_page: 1, per_page: 15, total: 0 });
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<string | null>(null);

    /* ── Filters (applied immediately) ── */
    const [filterStudent, setFilterStudent] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterDate, setFilterDate] = useState('');

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    /* ── Student options for the dropdown ── */
    const [studentOptions, setStudentOptions] = useState<SelectOption[]>([]);

    /* ── Detail modal ── */
    const [selectedPass, setSelectedPass] = useState<GatePassApiRecord | null>(null);

    /* ── Load students once for the filter dropdown ── */
    useEffect(() => {
        fetchStudents({ perPage: 200 })
            .then((res) => {
                const opts = res.data.map((s: StudentApiRecord) => {
                    const ui = mapStudentToUi(s);
                    const label = ui.enrollment_number
                        ? `${ui.student_name} (${ui.enrollment_number})`
                        : ui.student_name;
                    return { value: String(ui.id), label };
                });
                setStudentOptions(opts);
            })
            .catch(() => { /* student filter stays empty on failure */ });
    }, []);

    /* ── Data fetching ── */
    const loadGatePasses = useCallback(async () => {
        setLoading(true);
        setApiError(null);
        try {
            const res = await fetchGatePasses({
                page,
                perPage: pageSize,
                studentId: filterStudent || undefined,
                status: filterStatus || undefined,
                passType: filterType || undefined,
                validDate: filterDate || undefined,
            });
            setGatePasses(res.data ?? []);
            if (res.meta) setMeta(res.meta);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setApiError(apiErr.message || t('gatePass.failedToLoad'));
            setGatePasses([]);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, filterStudent, filterStatus, filterType, filterDate, t]);

    useEffect(() => { loadGatePasses(); }, [loadGatePasses]);

    /* Reset to the first page whenever a filter changes (single, race-free fetch). */
    const setFilterAndReset = (setter: (v: string) => void) => (v: string) => {
        setter(v);
        setPage(1);
    };

    const hasActiveFilters = !!(filterStudent || filterStatus || filterType || filterDate);
    const handleClear = () => {
        setFilterStudent('');
        setFilterStatus('');
        setFilterType('');
        setFilterDate('');
        setPage(1);
    };

    /* ── Dropdown option lists ── */
    const studentSelectOptions: SelectOption[] = [
        { value: '', label: t('gatePass.allStudents') },
        ...studentOptions,
    ];
    const statusSelectOptions: SelectOption[] = [
        { value: '', label: t('gatePass.allStatuses') },
        ...GATE_PASS_STATUS_OPTIONS,
    ];
    const typeSelectOptions: SelectOption[] = [
        { value: '', label: t('gatePass.allTypes') },
        ...GATE_PASS_TYPE_OPTIONS,
    ];

    /* ── Pagination ── */
    const pageNumbers = (): number[] => {
        const pages: number[] = [];
        const start = Math.max(1, meta.current_page - 2);
        const end = Math.min(meta.last_page, meta.current_page + 2);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    /* ── Badges ── */
    const statusBadge = (status: string | null | undefined) => {
        if (!status) return <span className="gatepass__muted">—</span>;
        return (
            <span className={`gatepass__badge gatepass__badge--${status}`}>
                {gatePassStatusLabel(status)}
            </span>
        );
    };

    /* ── Print ── */
    const handlePrint = (gp: GatePassApiRecord) => {
        openGatePassPrintWindow({
            schoolName: gp.school?.name || 'Gurukul Academy',
            studentName: studentNameOf(gp),
            enrollmentNumber: gp.student?.enrollment_number ?? null,
            className: gp.student?.current_class?.name ?? null,
            sectionName: gp.student?.current_section?.name ?? null,
            photoUrl: gp.gatepass_photo_url ?? gp.gatepass_photo_thumb_url ?? null,
            status: gatePassStatusLabel(gp.status),
            passType: gatePassTypeLabel(gp.pass_type),
            reason: gp.reason ? humanizeGatePassValue(gp.reason) : gatePassTypeLabel(gp.pass_type),
            expectedExit: formatTime(gp.expected_exit_time),
            expectedReturn: gp.expected_return_time ? formatTime(gp.expected_return_time) : null,
            authorizedName: gp.authorized_person_name ?? null,
            authorizedRelation: gp.authorized_person_relation ? humanizeGatePassValue(gp.authorized_person_relation) : null,
            authorizedContact: gp.authorized_person_contact ?? null,
            validDate: gp.valid_date ?? null,
        });
    };

    const fromRow = meta.from ?? (meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1);
    const toRow = meta.total === 0 ? 0 : Math.min(fromRow + meta.per_page - 1, meta.total);

    return (
        <div className="gatepass">
            {/* ── Header ── */}
            <div className="gatepass__page-header">
                <div>
                    <h1 className="gatepass__page-title">{t('gatePass.title')}</h1>
                    <p className="gatepass__page-subtitle">{t('gatePass.listSubtitle')}</p>
                </div>
            </div>

            {/* ── Error Banner ── */}
            {apiError && (
                <div className="gatepass__error-banner" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                    </svg>
                    <span>{apiError}</span>
                    <button className="gatepass__error-dismiss" onClick={() => setApiError(null)}>×</button>
                </div>
            )}

            {/* ── Toolbar / Filters ── */}
            <div className="gatepass__toolbar">
                <div className="gatepass__toolbar-left">
                    <SearchableSelect
                        id="gp-filter-student"
                        options={studentSelectOptions}
                        value={filterStudent}
                        onChange={setFilterAndReset(setFilterStudent)}
                        placeholder={t('gatePass.allStudents')}
                        searchPlaceholder={t('gatePass.searchStudent')}
                    />
                    <SearchableSelect
                        id="gp-filter-status"
                        options={statusSelectOptions}
                        value={filterStatus}
                        onChange={setFilterAndReset(setFilterStatus)}
                        placeholder={t('gatePass.allStatuses')}
                        searchable={false}
                    />
                    <SearchableSelect
                        id="gp-filter-type"
                        options={typeSelectOptions}
                        value={filterType}
                        onChange={setFilterAndReset(setFilterType)}
                        placeholder={t('gatePass.allTypes')}
                        searchable={false}
                    />
                    <input
                        className="gatepass__filter-date"
                        type="date"
                        value={filterDate}
                        onChange={(e) => { setFilterDate(e.target.value); setPage(1); }}
                        aria-label={t('gatePass.validDate')}
                    />
                    {hasActiveFilters && (
                        <button className="gatepass__clear-btn" onClick={handleClear} title={t('staffMgmt.clearFilters')}>
                            {t('common.clear')}
                        </button>
                    )}
                </div>

                <div className="gatepass__toolbar-right">
                    <span className="gatepass__record-count">
                        {t('gatePass.passCount', { count: meta.total })}
                    </span>
                </div>
            </div>

            {/* ── Data Grid ── */}
            <div className="gatepass__table-wrap">
                <table className="gatepass__table">
                    <thead>
                        <tr>
                            <th className="gatepass__th">{t('gatePass.student')}</th>
                            <th className="gatepass__th">{t('gatePass.passType')}</th>
                            <th className="gatepass__th">{t('gatePass.reason')}</th>
                            <th className="gatepass__th gatepass__th--date">{t('gatePass.validDate')}</th>
                            <th className="gatepass__th gatepass__th--time">{t('gatePass.timeOut')}</th>
                            <th className="gatepass__th">{t('gatePass.authorizedBy')}</th>
                            <th className="gatepass__th gatepass__th--status">{t('gatePass.status')}</th>
                            <th className="gatepass__th gatepass__th--actions">{t('common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={8} className="gatepass__loading-cell">
                                    <div className="gatepass__loader">
                                        <span className="gatepass__loader-dot" />
                                        <span className="gatepass__loader-dot" />
                                        <span className="gatepass__loader-dot" />
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && gatePasses.length === 0 && (
                            <tr>
                                <td colSpan={8} className="gatepass__empty-cell">
                                    <div className="gatepass__empty">
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                        <p>{t('gatePass.noPasses')}</p>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && gatePasses.map((gp) => {
                            const meta2 = studentMetaOf(gp);
                            return (
                                <tr key={gp.id} className="gatepass__row">
                                    <td className="gatepass__td">
                                        <span className="gatepass__student-name">{studentNameOf(gp)}</span>
                                        {meta2 && <span className="gatepass__sub">{meta2}</span>}
                                    </td>
                                    <td className="gatepass__td">
                                        {gp.pass_type
                                            ? <span className="gatepass__type">{gatePassTypeLabel(gp.pass_type)}</span>
                                            : <span className="gatepass__muted">—</span>}
                                    </td>
                                    <td className="gatepass__td">
                                        {gp.reason ? humanizeGatePassValue(gp.reason) : <span className="gatepass__muted">—</span>}
                                    </td>
                                    <td className="gatepass__td gatepass__td--date">{formatDate(gp.valid_date)}</td>
                                    <td className="gatepass__td gatepass__td--time">{formatTime(gp.expected_exit_time)}</td>
                                    <td className="gatepass__td">
                                        {gp.authorized_person_name
                                            ? <>
                                                {gp.authorized_person_name}
                                                {gp.authorized_person_relation && (
                                                    <span className="gatepass__sub">{humanizeGatePassValue(gp.authorized_person_relation)}</span>
                                                )}
                                            </>
                                            : <span className="gatepass__muted">—</span>}
                                    </td>
                                    <td className="gatepass__td gatepass__td--status">{statusBadge(gp.status)}</td>
                                    <td className="gatepass__td gatepass__td--actions">
                                        <button
                                            className="gatepass__view-btn"
                                            onClick={() => setSelectedPass(gp)}
                                            title={t('gatePass.viewDetails')}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                            </svg>
                                            {t('gatePass.view')}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            {!loading && meta.total > 0 && (
                <div className="gatepass__pagination">
                    <div className="gatepass__pagination-left">
                        <label className="gatepass__page-size-label">
                            {t('gatePass.rowsPerPage')}
                            <select className="gatepass__page-size" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                {PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </label>
                        <span className="gatepass__page-info">
                            {t('gatePass.showingRange', { from: fromRow, to: toRow, total: meta.total })}
                        </span>
                    </div>
                    <div className="gatepass__pagination-right">
                        <button className="gatepass__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage(1)} title="First page">«</button>
                        <button className="gatepass__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage((p) => p - 1)} title="Previous page">‹</button>
                        {pageNumbers().map((n) => (
                            <button
                                key={n}
                                className={`gatepass__page-btn ${n === meta.current_page ? 'gatepass__page-btn--active' : ''}`}
                                onClick={() => setPage(n)}
                            >{n}</button>
                        ))}
                        <button className="gatepass__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage((p) => p + 1)} title="Next page">›</button>
                        <button className="gatepass__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage(meta.last_page)} title="Last page">»</button>
                    </div>
                </div>
            )}

            {/* ── Detail Modal ── */}
            {selectedPass && (
                <>
                    <div className="gatepass__modal-backdrop" onClick={() => setSelectedPass(null)} />
                    <div className="gatepass__modal" role="dialog" aria-modal="true">
                        <div className="gatepass__modal-header">
                            <h3 className="gatepass__modal-title">{t('gatePass.detailsTitle')}</h3>
                            <button className="gatepass__modal-close" onClick={() => setSelectedPass(null)} aria-label={t('common.close')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <div className="gatepass__modal-student">
                            <span className="gatepass__modal-student-name">{studentNameOf(selectedPass)}</span>
                            {studentMetaOf(selectedPass) && (
                                <span className="gatepass__modal-student-meta">{studentMetaOf(selectedPass)}</span>
                            )}
                        </div>

                        {(selectedPass.gatepass_photo_thumb_url || selectedPass.gatepass_photo_url) && (
                            <div className="gatepass__modal-photo">
                                <img
                                    src={selectedPass.gatepass_photo_thumb_url ?? selectedPass.gatepass_photo_url ?? undefined}
                                    alt={t('gatePass.photoAlt')}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                            </div>
                        )}

                        <div className="gatepass__modal-body">
                            <div className="gatepass__detail-row">
                                <span className="gatepass__detail-label">{t('gatePass.status')}</span>
                                <span className="gatepass__detail-value">{statusBadge(selectedPass.status)}</span>
                            </div>
                            <div className="gatepass__detail-row">
                                <span className="gatepass__detail-label">{t('gatePass.passType')}</span>
                                <span className="gatepass__detail-value">{gatePassTypeLabel(selectedPass.pass_type)}</span>
                            </div>
                            <div className="gatepass__detail-row">
                                <span className="gatepass__detail-label">{t('gatePass.reason')}</span>
                                <span className="gatepass__detail-value">{selectedPass.reason ? humanizeGatePassValue(selectedPass.reason) : '—'}</span>
                            </div>
                            <div className="gatepass__detail-row">
                                <span className="gatepass__detail-label">{t('gatePass.validDate')}</span>
                                <span className="gatepass__detail-value">{formatDate(selectedPass.valid_date)}</span>
                            </div>
                            <div className="gatepass__detail-row">
                                <span className="gatepass__detail-label">{t('gatePass.timeOut')}</span>
                                <span className="gatepass__detail-value">{formatTime(selectedPass.expected_exit_time)}</span>
                            </div>
                            {selectedPass.expected_return_time && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.expectedReturn')}</span>
                                    <span className="gatepass__detail-value">{formatTime(selectedPass.expected_return_time)}</span>
                                </div>
                            )}
                            {selectedPass.actual_exit_time && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.actualExit')}</span>
                                    <span className="gatepass__detail-value">{formatTime(selectedPass.actual_exit_time)}</span>
                                </div>
                            )}
                            {selectedPass.actual_return_time && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.actualReturn')}</span>
                                    <span className="gatepass__detail-value">{formatTime(selectedPass.actual_return_time)}</span>
                                </div>
                            )}
                            <div className="gatepass__detail-row">
                                <span className="gatepass__detail-label">{t('gatePass.authorizedBy')}</span>
                                <span className="gatepass__detail-value">
                                    {selectedPass.authorized_person_name
                                        ? `${selectedPass.authorized_person_name}${selectedPass.authorized_person_relation ? ` (${humanizeGatePassValue(selectedPass.authorized_person_relation)})` : ''}`
                                        : '—'}
                                </span>
                            </div>
                            {selectedPass.authorized_person_contact && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.contact')}</span>
                                    <span className="gatepass__detail-value">{selectedPass.authorized_person_contact}</span>
                                </div>
                            )}
                            {parentNameOf(selectedPass) && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.parent')}</span>
                                    <span className="gatepass__detail-value">{parentNameOf(selectedPass)}</span>
                                </div>
                            )}
                            {parentContactOf(selectedPass) && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.parentContact')}</span>
                                    <span className="gatepass__detail-value">{parentContactOf(selectedPass)}</span>
                                </div>
                            )}
                            {selectedPass.requested_by?.name && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.requestedBy')}</span>
                                    <span className="gatepass__detail-value">{selectedPass.requested_by.name}</span>
                                </div>
                            )}
                            {selectedPass.approved_by?.name && (
                                <div className="gatepass__detail-row">
                                    <span className="gatepass__detail-label">{t('gatePass.approvedBy')}</span>
                                    <span className="gatepass__detail-value">{selectedPass.approved_by.name}</span>
                                </div>
                            )}
                        </div>

                        <div className="gatepass__modal-actions">
                            <button className="gatepass__modal-btn gatepass__modal-btn--cancel" onClick={() => setSelectedPass(null)}>
                                {t('common.close')}
                            </button>
                            <button className="gatepass__modal-btn gatepass__modal-btn--submit" onClick={() => handlePrint(selectedPass)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
                                </svg>
                                {t('gatePass.print')}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
